import React, { useEffect, useState } from 'react';
import { Input } from 'antd';
import { HighlightOutlined, ClearOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../../Shared/GlobalContext/ThemeContext';
import BeautifyControl from '../../../../Shared/BeautifyControl/BeautifyControl';

const { TextArea } = Input;

const FooterInput = ({
    message,
    setMessage,
    handleKeyDown,
    updateSelectionFromEvent,
    isRecording,
    pendingVoice,
    textAreaRef,
    focusInputRef,
    mobileButtonStyle,
    buttonStyle,
    beautifying,
    runBeautifyMessage,
    iconSize,
    conversationId,
    beautifyResetSignal
}) => {
    const { t } = useTranslation();
    const { isDarkMode } = useTheme();
    const [isBeautified, setIsBeautified] = useState(false);

    const finalButtonStyle = buttonStyle || mobileButtonStyle;

    // Expose a focus method to parent via ref
    useEffect(() => {
        if (!focusInputRef || typeof focusInputRef !== 'object') return;
        focusInputRef.current = () => {
            try {
                const input = textAreaRef.current?.resizableTextArea?.textArea || textAreaRef.current?.input || textAreaRef.current;
                if (input && typeof input.focus === 'function') {
                    input.focus();
                }
            } catch (e) { }
        };
        return () => { focusInputRef.current = null; };
    }, [focusInputRef, textAreaRef]);

    const hasText = !!(message && message.trim());

    if (isRecording || pendingVoice) return null;

    return (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', minWidth: 0 }}>
            <TextArea
                ref={textAreaRef}
                value={message}
                onChange={(e) => { setMessage(e.target.value); updateSelectionFromEvent(e); }}
                onSelect={updateSelectionFromEvent}
                onClick={updateSelectionFromEvent}
                onKeyUp={updateSelectionFromEvent}
                onKeyDown={handleKeyDown}
                placeholder={t('start_a_message')}
                rows={1}
                autoSize={message ? { minRows: 1, maxRows: 3 } : false}
                dir="auto"
                style={{
                    width: '100%',
                    border: 'none',
                    resize: 'none',
                    padding: `6px ${!isBeautified ? '36px' : '0'} 6px 0`,
                    fontSize: '15px',
                    lineHeight: '20px',
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    color: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : undefined,
                    unicodeBidi: 'plaintext'
                }}
            />
            {/* Beautify bar: inline right state when inactive, full bottom row when active */}
            <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: isBeautified ? 2 : 0,
                ...(!isBeautified ? {
                    position: 'absolute',
                    right: 0,
                    bottom: 0, // Align exactly to the bottom right of the textarea
                    zIndex: 10
                } : {})
            }}>
                <BeautifyControl
                    value={message}
                    onChange={setMessage}
                    runBeautify={runBeautifyMessage}
                    beautifyLabel={t('beautify')}
                    revertLabel={t('revert')}
                    size="small"
                    disabled={beautifying}
                    allowEmpty={true}
                    beautifyIcon={<HighlightOutlined style={{ fontSize: iconSize }} />}
                    revertIcon={<ClearOutlined style={{ fontSize: iconSize }} />}
                    buttonStyle={{ ...finalButtonStyle, color: '#7F56D9' }}
                    showMeta={true}
                    onStateChange={setIsBeautified}
                    resetSignal={beautifyResetSignal}
                />
            </div>
        </div>
    );
};

export default FooterInput;
